import { PDFDocument } from "pdf-lib"
import { loadBytes, loadNoPhotoImage } from "./assetLoader"
import { pageHeight, pageWidth, pdfAuthor, pdfCreator } from "./constants"
import { embedFontSet, IFontSet } from "./fontSet"
import { drawImageInfoPages } from "./renderers/imageInfoPage"
import { drawImagePage } from "./renderers/imagePage"
import { drawOrgCover, drawViewsCover, drawWCImageCover, ICoverStrings } from "./renderers/coverPage"
import { drawWhiteBackground } from "./renderers/shared"
import { drawWCImageInfoPages } from "./renderers/wcImageInfoPage"
import { IPdfOptions, IPdfPage, IPdfRow, TPdfLayoutType, TPdfPageType } from "./types"

export interface IPdfStrings extends ICoverStrings {
	/** localized replacement for "plus applicable taxes" (italic range in WC layout) */
	taxMessage: string
}

export interface IPageAssets {
	/** artwork image as JPEG bytes (already q0.85-encoded); null = use no-photo fallback */
	imageJpeg: Uint8Array | null
	/** attachment PDF bytes (attachment pages only) */
	attachmentPdf?: Uint8Array | null
}

function newDocument(): Promise<PDFDocument> {
	return PDFDocument.create().then((doc) => {
		doc.setCreator(pdfCreator)
		doc.setAuthor(pdfAuthor)
		return doc
	})
}

function layoutTypeOf(type: TPdfPageType): TPdfLayoutType {
	if (type.kind === "wcCover" || type.kind === "wcImageCover" || type.kind === "wcImageInfo") {
		return "wc"
	}
	if (type.kind === "cover" || type.kind === "imageInfo") {
		return type.layoutType
	}
	return ""
}

/**
 * Renders one page type into `doc` (adds the page itself; info pages may add
 * continuation pages). Attachment pages are NOT handled here — they are
 * embedded verbatim by the caller.
 */
async function renderPageInto(
	doc: PDFDocument,
	type: Exclude<TPdfPageType, { kind: "attachment" }>,
	imageJpeg: Uint8Array | null,
	options: IPdfOptions,
	customInput: string,
	strings: IPdfStrings,
	fonts: IFontSet
): Promise<void> {
	const page = doc.addPage([pageWidth, pageHeight])

	switch (type.kind) {
		case "cover": {
			if (type.templateBytes) {
				await drawOrgCover(doc, page, customInput, type.templateBytes, type.layoutType, fonts)
			} else {
				drawViewsCover(doc, page, customInput, type.layoutType, fonts, strings)
			}
			break
		}
		case "wcCover": {
			const title = customInput.toUpperCase()
			if (type.templateBytes) {
				await drawOrgCover(doc, page, title, type.templateBytes, "wc", fonts, { wcLinkRect: true })
			} else {
				drawViewsCover(doc, page, title, "wc", fonts, strings)
			}
			break
		}
		case "wcImageCover": {
			await drawWCImageCover(doc, page, customInput.toUpperCase(), imageJpeg, type.templateBytes, fonts)
			break
		}
		case "image": {
			if (imageJpeg) {
				await drawImagePage(doc, page, imageJpeg, type.fill)
			} else {
				drawWhiteBackground(page)
			}
			break
		}
		case "imageInfo": {
			await drawImageInfoPages(
				doc,
				page,
				type.item,
				options,
				customInput,
				type.templateBytes,
				type.layoutType,
				fonts,
				imageJpeg
			)
			break
		}
		case "wcImageInfo": {
			await drawWCImageInfoPages(
				doc,
				page,
				type.item,
				options,
				customInput,
				type.templateBytes,
				fonts,
				imageJpeg,
				strings.taxMessage
			)
			break
		}
	}
}

/**
 * Port of iOS WorkPDFGenerator.generate — renders ONE page type into its own
 * document (used by the customise-screen thumbnails). Returns null for
 * unusable input (parity: iOS returns nil).
 */
export async function generatePage(
	type: TPdfPageType,
	assets: IPageAssets,
	options: IPdfOptions,
	customInput: string,
	strings: IPdfStrings
): Promise<Uint8Array | null> {
	// iOS: contentImage = image ?? no-photo — every page type falls back to the placeholder
	const imageJpeg = assets.imageJpeg ?? (await loadNoPhotoImage())

	if (type.kind === "attachment") {
		if (!assets.attachmentPdf) {
			return null
		}
		return assets.attachmentPdf
	}

	const doc = await newDocument()
	const fonts = await embedFontSet(doc, layoutTypeOf(type))
	await renderPageInto(doc, type, imageJpeg, options, customInput, strings, fonts)
	return doc.save()
}

/**
 * Port of iOS normalizedBackCoverPDF — every page of the source PDF is
 * aspect-fit centered on a white A4 page.
 */
export async function normalizeBackCover(bytes: Uint8Array): Promise<Uint8Array | null> {
	try {
		const source = await PDFDocument.load(new Uint8Array(bytes))
		if (source.getPageCount() === 0) {
			return null
		}
		const doc = await newDocument()
		const indices = source.getPageIndices()
		const embedded = await doc.embedPdf(new Uint8Array(bytes), indices)
		embedded.forEach((embeddedPage) => {
			const page = doc.addPage([pageWidth, pageHeight])
			drawWhiteBackground(page)
			const scale = Math.min(pageWidth / embeddedPage.width, pageHeight / embeddedPage.height)
			const drawWidth = embeddedPage.width * scale
			const drawHeight = embeddedPage.height * scale
			page.drawPage(embeddedPage, {
				x: (pageWidth - drawWidth) / 2,
				y: (pageHeight - drawHeight) / 2,
				xScale: scale,
				yScale: scale,
			})
		})
		return doc.save()
	} catch {
		return null
	}
}

export interface ISelectedPageRef {
	row: IPdfRow
	page: IPdfPage
}

/** iOS: selected (row, page) pairs in row order. */
export function selectedPages(contentRows: IPdfRow[]): ISelectedPageRef[] {
	const out: ISelectedPageRef[] = []
	contentRows.forEach((row) => {
		row.pages
			.filter((p) => p.isSelected)
			.forEach((page) => {
				out.push({ row, page })
			})
	})
	return out
}

export interface IAssembleInput {
	coverRow: IPdfRow
	contentRows: IPdfRow[]
	options: IPdfOptions
	strings: IPdfStrings
	/** org layout type — selects the font set embedded into the document */
	layoutType: TPdfLayoutType
	backCoverBytes: Uint8Array | null
	/** resolves the (possibly downscaled/re-encoded) image bytes for a row */
	loadRowImage: (row: IPdfRow) => Promise<Uint8Array | null>
	/** resolves attachment PDF bytes */
	loadAttachment: (pdfPath: string) => Promise<Uint8Array | null>
	onProgress?: (done: number, total: number) => void
}

export interface IAssembledPdf {
	bytes: Uint8Array
	pageCount: number
	selectedPageCount: number
	/**
	 * Count of DISTINCT artwork images that had a URL but failed to load (e.g.
	 * blocked by CORS / network) and were rendered as the no-photo placeholder.
	 * Surfaced so the UI can warn instead of silently exporting placeholder pages.
	 */
	missingImageCount: number
}

/**
 * Port of the iOS previewPDF assembly: cover -> selected pages in row order ->
 * normalized back cover. Unlike iOS (which merges per-page documents), all
 * pages are drawn into ONE document so each font is embedded exactly once —
 * this matters because CFF (OTF) fonts are embedded whole (see fontManager).
 */
export async function assembleDocument(input: IAssembleInput): Promise<IAssembledPdf> {
	const { coverRow, contentRows, options, strings, layoutType, backCoverBytes, onProgress } = input

	const pages = selectedPages(contentRows)
	const coverTitle = coverRow.customInput
	const coverPage = coverRow.pages.find((p) => p.isSelected) ?? null

	// iOS quirk kept for parity: the denominator counts the title, not the cover
	const total = pages.length + (coverTitle === "" ? 0 : 1) + (backCoverBytes === null ? 0 : 1)
	let done = 0
	const tick = () => {
		done += 1
		onProgress?.(Math.min(done, total), total)
	}

	const merged = await newDocument()
	const fonts = await embedFontSet(merged, layoutType)

	// Track artwork images that were expected but failed to load (placeholder
	// substituted). Keyed by URL so 3 selected variants of one image count once.
	// `usesImage` is false for text-only covers, which have an imageUrl on the
	// row but never draw it.
	const missingImageUrls = new Set<string>()
	const loadImageFor = async (row: IPdfRow, usesImage: boolean): Promise<Uint8Array | null> => {
		const loaded = await input.loadRowImage(row)
		if (loaded) {
			return loaded
		}
		const expectedUrl = row.pages[0]?.imageUrl?.url
		if (usesImage && expectedUrl) {
			missingImageUrls.add(expectedUrl)
		}
		// null (not empty bytes) when even the placeholder is unavailable — the
		// renderers treat null as "draw the NO IMAGE page" while empty bytes
		// would reach embedJpg and fail the whole document
		return loadNoPhotoImage()
	}

	const appendPdf = async (bytes: Uint8Array | null) => {
		if (!bytes) {
			return
		}
		try {
			const src = await PDFDocument.load(new Uint8Array(bytes))
			const copied = await merged.copyPages(src, src.getPageIndices())
			copied.forEach((p) => merged.addPage(p))
		} catch {
			// skip unusable documents (parity with iOS addOrInsert ignoring nils)
		}
	}

	// cover (iOS: only when the cover page is selected)
	if (coverPage && coverPage.type.kind !== "attachment") {
		const coverImage = await loadImageFor(coverRow, coverPage.type.kind === "wcImageCover")
		await renderPageInto(merged, coverPage.type, coverImage, options, coverTitle, strings, fonts)
		tick()
	}

	// content pages in row order
	for (const { row, page } of pages) {
		if (row.rowType === "artwork" && page.type.kind !== "attachment") {
			const imageJpeg = await loadImageFor(row, true)
			await renderPageInto(merged, page.type, imageJpeg, options, row.customInput, strings, fonts)
			tick()
		} else if (row.rowType === "attachment" && page.type.kind === "attachment") {
			const attachmentPdf = await input.loadAttachment(page.type.attachment.pdfPath)
			if (attachmentPdf) {
				await appendPdf(attachmentPdf)
			}
			tick()
		}
	}

	// back cover
	if (backCoverBytes) {
		const normalized = await normalizeBackCover(backCoverBytes)
		await appendPdf(normalized)
		tick()
	}

	const bytes = await merged.save()
	return {
		bytes,
		pageCount: merged.getPageCount(),
		selectedPageCount: pages.length,
		missingImageCount: missingImageUrls.size,
	}
}

/** Convenience for the hardcoded back covers (fetch + cache via assetLoader). */
export function loadBackCover(url: string | null): Promise<Uint8Array | null> {
	return url ? loadBytes(url) : Promise.resolve(null)
}
